import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Checkbox } from '@/shared/ui/checkbox';
import { PolicySelect, requiresExplicitVerificationPolicy } from '@/modules/agreements/shared';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';
import type { AgreementRequiredAction } from '@/api/schemas/agreements.types';

const ACTION_OPTIONS = [
  { value: 'acknowledgment', label: 'Acknowledgment' },
  { value: 'consent', label: 'Consent' },
  { value: 'signature', label: 'Signature' },
];

/**
 * Step 3 — the requirement that makes the document binding.
 *
 * A document with no requirement is inert: nothing asks anyone to act on it. The
 * requirement binds a subject to a SPECIFIC version, which is why this step can
 * only exist after step 2 has produced one.
 *
 * The action is asked again rather than inherited from step 1, because the two
 * are genuinely different questions: step 1 says what the document IS, this says
 * what a subject must DO about it. They usually match, and the operator can see
 * that they do.
 */
export function RequirementStep(props: {
  draft: DocumentDraft;
  onChange: (patch: Partial<DocumentDraft>) => void;
}): ReactNode {
  const { draft, onChange } = props;
  const audience = draft.audience === 'both' ? 'tenant' : draft.audience;
  const explicitRequired = requiresExplicitVerificationPolicy(audience, draft.requirementAction);

  return (
    <div className="space-y-4">
      <Select
        label="Required action"
        options={ACTION_OPTIONS}
        value={draft.requirementAction}
        onChange={(event) =>
          onChange({ requirementAction: event.target.value as AgreementRequiredAction })
        }
        hint="What a subject must do to satisfy this requirement."
        required
      />
      <PolicySelect
        policy={draft.verificationPolicy}
        explicitRequired={explicitRequired}
        policyChosen={draft.verificationPolicy !== ''}
        onChange={(policy) => onChange({ verificationPolicy: policy })}
      />
      <Checkbox
        label="Re-acceptance required when a new version is published"
        checked={draft.reacceptanceRequired}
        onChange={(event) => onChange({ reacceptanceRequired: event.target.checked })}
        hint="When on, publishing a new version asks every bound subject to act again."
        optional
      />
      <Input
        label="Deadline"
        type="date"
        value={draft.deadlineAt}
        onChange={(event) => onChange({ deadlineAt: event.target.value })}
        hint="Leave blank for no deadline."
        optional
      />
    </div>
  );
}
